import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

export enum AIQuestionType {
  CODING = 'Coding',
  OBJECTIVE = 'Objective',
  VIDEO = 'Subjective Video',
}

export interface language {
  name: string;
  id: number;
  onlineJudgeId: number;
  monaco_id: string;
}

interface IVideoQuestionUpdateType {
  attemptLeft: number;
  lastAttempt: null | {
    video: string;
    transcription: string;
  };
  storageData: null | {
    location: string;
    env: 'HH_API' | null;
  };
  maxTimeAllowed: number;
  lastAttemptSubmitted: boolean;
}

export interface testcase {
  test_case_id: string;
  input: string;
  output: string;
  score: string;
}

export interface AIQuestionModal {
  question_id: string;
  index: number;
  title: string;
  description: string;
  choice: { option: string; value: string }[];
  answer: string;
  time_taken: number;
  start_time: string;
  langauges: language[];
}

export interface code {
  tail: string;
  head_body: string;
}

export interface AIQuestionState {
  question_id: string;
  index: number;
  title: string;
  description: string;
  choice: { option: string; value: string }[];
  answer: string;
  time_taken: number;
  start_time: string;
  question_type: AIQuestionType;
  code?: code;
  test_cases?: testcase[];
  coding_languages?: language[];
  attemptLeft: number;
  lastAttempt: null | {
    video: string;
    transcription: string;
  };
  attendee_id: number | undefined;
  storageData: null | {
    location: string;
    env: 'HH_API' | null;
  };
  maxTimeAllowed: number;
  lastAttemptSubmitted: boolean;
  videoQuestionState: VideoQuestionState;
  cameraAvailable: boolean;
  repeatQuestion: RepeatQuestionState;
  currentTab: string;
  dirty: boolean;
}

export interface testcase {
  test_case_id: string;
  input: string;
  output: string;
  score: string;
  is_sample: boolean;
}

export enum RepeatQuestionState {
  DISABLED = 'DISABLED',
  ENABLED = 'ENABLED',
}

export enum VideoQuestionState {
  QUESTION_NOT_VISIBLE = 'QUESTION_NOT_VISIBLE',
  QUESTION_VISIBLE = 'QUESTION_VISIBLE',
  SKIPPED = 'SKIPPED',
  SUBMITTED = 'SUBMITTED',
}

const initialState: AIQuestionState = {
  question_id: '',
  question_type: AIQuestionType.CODING,
  index: 0,
  title: '',
  description: '',
  dirty: false,
  choice: [
    {
      option: 'a',
      value: '',
    },
    {
      option: 'b',
      value: '',
    },
    {
      option: 'c',
      value: '',
    },
    {
      option: 'd',
      value: '',
    },
  ],
  answer: '',
  time_taken: 0,
  start_time: moment().unix().toString(),
  attemptLeft: 0,
  lastAttempt: null,
  storageData: null,
  maxTimeAllowed: 10,
  lastAttemptSubmitted: false,
  videoQuestionState: VideoQuestionState.QUESTION_NOT_VISIBLE,
  attendee_id: undefined,
  cameraAvailable: false,
  repeatQuestion: RepeatQuestionState.DISABLED,
  currentTab: '',
};

export const AIQuestionSlice = createSlice({
  name: 'aiquestion',
  initialState,
  reducers: {
    setAIQuestionInfo: (state, action: PayloadAction<AIQuestionState>) => {
      const {
        question_id,
        index,
        title,
        description,
        choice,
        answer,
        time_taken,
        code,
        test_cases,
        coding_languages,
        question_type,
        attemptLeft = 0,
        lastAttempt = null,
        storageData = null,
        maxTimeAllowed = 10,
        lastAttemptSubmitted,
        videoQuestionState = VideoQuestionState.QUESTION_NOT_VISIBLE,
      } = action.payload;
      state.attemptLeft = attemptLeft;
      state.lastAttempt = lastAttempt;
      state.storageData = storageData;
      state.maxTimeAllowed = maxTimeAllowed;
      state.lastAttemptSubmitted = lastAttemptSubmitted;
      state.question_id = question_id;
      state.index = index;
      state.title = title;
      state.description = description;
      state.choice = choice;
      state.answer = answer;
      state.time_taken = time_taken;
      state.code = code;
      state.question_type = question_type;
      state.coding_languages = coding_languages;
      state.test_cases = test_cases;
      state.videoQuestionState = videoQuestionState;
    },
    setAIQuestionId: (state, action: PayloadAction<string>) => {
      state.question_id = action.payload;
    },
    setAIQuestionAnswer: (state, action: PayloadAction<string>) => {
      state.answer = action.payload;
    },
    setAIAttendeeId: (state, action: PayloadAction<number | undefined>) => {
      state.attendee_id = action.payload;
    },
    setAICameraAvailable: (state, action: PayloadAction<boolean>) => {
      state.cameraAvailable = action.payload;
    },
    setAIQuestionDirty: (state, action: PayloadAction<boolean>) => {
      state.dirty = action.payload;
    },
    setAIQuestionStartTime: (state, action: PayloadAction<string>) => {
      state.start_time = action.payload;
    },
    setAIRepeatQuestionState: (
      state,
      action: PayloadAction<RepeatQuestionState>
    ) => {
      state.repeatQuestion = action.payload;
    },
    setAICurrentTab: (state, action: PayloadAction<string>) => {
      state.currentTab = action.payload;
    },
    setAIVideoQuestionUpdate: (
      state,
      action: PayloadAction<IVideoQuestionUpdateType>
    ) => {
      const {
        attemptLeft,
        lastAttempt,
        storageData,
        maxTimeAllowed,
        lastAttemptSubmitted,
      } = action.payload;
      state.attemptLeft = attemptLeft;
      state.lastAttempt = lastAttempt;
      state.storageData = storageData;
      state.maxTimeAllowed = maxTimeAllowed;
      state.lastAttemptSubmitted = lastAttemptSubmitted;
    },
  },
});

export const {
  setAIQuestionInfo,
  setAIQuestionAnswer,
  setAIQuestionStartTime,
  setAIAttendeeId,
  setAICurrentTab,
  setAIQuestionId,
  setAIQuestionDirty,
  setAIVideoQuestionUpdate,
  setAICameraAvailable,
} = AIQuestionSlice.actions;

export default AIQuestionSlice.reducer;
